import type { Table } from '@tanstack/react-table'
import toNumber from 'lodash/toNumber'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

import { Button } from '@klear/ui/components/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
}

export function DataTablePagination<TData>({ table }: DataTablePaginationProps<TData>) {
  const totalRows = table.getRowCount()
  const currentPageSize = table.getState().pagination.pageSize
  const numericOptions = [10, 20, 50]

  const ALL_PAGE_SIZE_MARKER = totalRows + 1

  const isAllSelected =
    currentPageSize === ALL_PAGE_SIZE_MARKER ||
    (currentPageSize >= totalRows && !numericOptions.includes(currentPageSize))
  const selectedValue = isAllSelected ? 'all' : `${currentPageSize}`

  const effectivePageSize = currentPageSize || 10

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">{totalRows} row(s)</div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={selectedValue}
            onValueChange={(value) => {
              if (value === 'all') {
                table.setPageSize(ALL_PAGE_SIZE_MARKER)
              } else {
                table.setPageSize(toNumber(value))
              }
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={effectivePageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {['10', '20', '50', 'all'].map((pageSize) => (
                <SelectItem key={pageSize} value={pageSize}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  )
}
